<template>
  <el-popover
    placement="bottom-end"
    :width="380"
    trigger="click"
    :show-arrow="false"
    popper-style="padding: 0; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1);"
    popper-class="v4-notification-popover"
  >
    <template #reference>
      <button
        type="button"
        class="relative inline-flex items-center justify-center w-9 h-9 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none"
      >
        <BellIcon class="w-5 h-5" />
        <span
          v-if="totalNotification > 0"
          :key="totalNotification"
          class="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full border-2 border-white dark:border-gray-900"
        >
          {{ totalNotification > 99 ? "99+" : totalNotification }}
        </span>
      </button>
    </template>

    <template #default>
      <div class="flex flex-col max-h-[450px] bg-white z-50">
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0"
        >
          <h2 class="text-sm font-semibold text-gray-900">Notifications</h2>
          <button
            v-if="notifications.length > 0 && !loading"
            type="button"
            class="text-xs font-medium text-blue-600 hover:text-blue-700"
            @click="markAllAsRead"
          >
            Mark all as read
          </button>
        </div>

        <el-scrollbar max-height="380px">
          <div v-if="loading" class="p-4">
            <el-skeleton :rows="4" animated />
          </div>

          <ul
            v-else-if="notifications.length > 0"
            role="list"
            class="divide-y divide-gray-100"
          >
            <li
              v-for="notification in notifications"
              :key="notification.id"
              class="relative flex gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
              :class="notification.read_at ? 'bg-white' : 'bg-blue-50/60'"
              @click="openPage(notification)"
            >
              <span
                class="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                :class="notification.read_at ? 'bg-transparent' : 'bg-blue-500'"
              />
              <p class="text-sm text-gray-700 line-clamp-2 leading-snug">
                {{
                  notification.message
                    ? notification.message
                    : notification.data.message
                }}
              </p>
            </li>
          </ul>

          <div v-else class="py-8">
            <el-empty :image-size="80" description="No notifications found" />
          </div>
        </el-scrollbar>
      </div>
    </template>
  </el-popover>
</template>

<script setup>
import { ElPopover, ElScrollbar, ElSkeleton, ElEmpty } from "element-plus";
import { BellIcon } from "@heroicons/vue/outline";
import { inject, ref, onMounted, computed } from "vue";
import Bugsnag from "@bugsnag/js";
import { useAuthStore } from "@/stores";
import EventBus from "@/plugins/eventBus";
import { useRouter } from "vue-router";
import Pusher from "pusher-js";
import { usePusherStore } from "@/stores/pusher";

const axios = inject("axios");
const notifications = ref([]);
const notifCounter = ref(0);
const loading = ref(true);
const router = useRouter();
const pusher = ref(null);
const pusherConfig = usePusherStore();

const totalNotification = computed(() => {
  return notifCounter.value;
});
const notifTotalItem = ref(0);

async function markAllAsRead() {
  await axios
    .post("/api/notifications/read-all")
    .then(() => {
      fetchAllNotifications();
    })
    .catch((error) => {
      Bugsnag.notify(error);
    });
}

function openPage(notification) {
  markAsRead(notification);
}

async function markAsRead(notification) {
  const { id } = notification;
  await axios
    .post("/api/notifications/read", { id })
    .then(() => {
      fetchAllNotifications();
      window.open(notification?.data?.link ?? notification.data.link, "_self");
    })
    .catch((error) => {
      Bugsnag.notify(error);
    });
}

async function fetchAllNotifications() {
  notifTotalItem.value += 10;
  loading.value = true;
  await axios
    .get("/api/notifications", {
      page: 1,
      totalPage: notifTotalItem.value,
    })
    .then((response) => {
      notifications.value = response.data.all.data;
      notifCounter.value = response.data.unread;
      loading.value = false;
    })
    .catch((error) => {
      Bugsnag.notify(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

const authUser = useAuthStore();

const setupNotificationChannel = () => {
  try {
    const channel = pusher.value.subscribe("notification-channel");

    channel.bind("App\\Events\\Notification", (notification) => {
      if (notification.user.id === authUser.user.id) {
        fetchAllNotifications();
      }
    });
  } catch (error) {
    Bugsnag.notify(error);
  }
};

const setupUserNotificationChannel = () => {
  try {
    const channel = pusher.value.subscribe(
      "private-App.Models.User." + authUser.user.id
    );

    channel.bind(
      "Illuminate\\Notifications\\Events\\BroadcastNotificationCreated",
      (notification) => {
        const link = notification.link;

        EventBus.$emit(
          "CREATE_NOTIFICATION",
          notification.message,
          "info",
          7000,
          link
        );

        if (notification.type === "App\\Notifications\\UnitTranscodeComplete") {
          handleUnitsTranscodeCompleteNotification();
        }

        if (
          notification.type ===
          "App\\Notifications\\ActiveRolePlayTranscodeComplete"
        ) {
          handleActiveRolePlayTranscodeCompleteNotification();
        }
      }
    );
  } catch (error) {
    Bugsnag.notify(error);
  }
};

const pusherKey = ref("");
const appleStore = ref("");

const setEnv = async () => {
  await axios.get("/api/get-env").then(({ data }) => {
    pusherConfig.setPusher(data);
    pusherKey.value = {
      pusher_cluster: data.pusher_cluster,
      pusher_key: data.pusher_key,
    };
    appleStore.value = data.apple_store;
  });
};

onMounted(async () => {
  await setEnv();
  pusher.value = new Pusher(pusherConfig.pusher.pusher_key, {
    cluster: pusherConfig.pusher.pusher_cluster,
    encrypted: true,
    authEndpoint: "/broadcasting/auth", // Default Laravel endpoint
    auth: {
      headers: {
        Authorization: `Bearer ${authUser.token}`, // If using token-based auth
      },
    },
  });

  setupNotificationChannel();
  setupUserNotificationChannel();
});

const handleActiveRolePlayTranscodeCompleteNotification = () => {
  if (router.currentRoute.value.name === "units") {
    EventBus.$emit("ACTIVE_ROLE_PLAY_REFRESH");
  }
};

const handleUnitsTranscodeCompleteNotification = () => {
  if (
    router.currentRoute.value.name === "units" ||
    router.currentRoute.value.name === "edit-unit" ||
    router.currentRoute.value.name === "edit-unit-course"
  ) {
    EventBus.$emit("UNIT_PAGE_REFRESH");
  }
};

onMounted(() => {
  fetchAllNotifications();
});
</script>
